import { HttpClient } from '@angular/common/http';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {BaseModel, ListFilter, ListResult, Pagination, SearchRequest, SearchResult} from '@shared/base';
import {ApiUtils} from '@shared/utils/api.utils';
import {ConfigUtil} from '@shared/services/config.util';

export abstract class BaseApi<M extends BaseModel> {
  protected endpoint: string;

  protected constructor(
    protected httpClient: HttpClient,
    path: string
  ) {
    this.endpoint = `${ConfigUtil.get('api')['backend']}/${path}`;
  }

  abstract create(): M;

  public findAll(): Observable<M[]> {
    return this.httpClient.get<M[]>(this.endpoint);
  }

  public findByUuid(uuid: string): Observable<M> {
    return this.httpClient.get<M>(`${this.endpoint}/${uuid}`).pipe(map(value => {
      return Object.assign(this.create(), value);
    }));
  }

  public findByUuids(uuidList: string[]): Observable<M[]> {
    return this.httpClient.get<M[]>(`${this.endpoint}/by/uuid`, {
      params: {
        uuid: uuidList
      }
    });
  }

  public findByFilter(listFilter: ListFilter, pagination: Pagination): Observable<ListResult<M>> {
    const httpParams = ApiUtils.createHttpParams({...listFilter, ...pagination.toListFilter()});
    return this.httpClient.get<ListResult<M>>(`${this.endpoint}/list`, {params: httpParams}).pipe(map(value => {
      return Object.assign(new ListResult<M>(), value);
    }));
  }

  public findByRequest(searchRequest: SearchRequest): Observable<SearchResult<M>> {
    return this.httpClient.post<SearchResult<M>>(`${this.endpoint}/search`, searchRequest);
  }

  public save(model: M): Observable<M> {
    return this.httpClient.post<M>(`${this.endpoint}`, model).pipe(map(value => {
      return Object.assign(this.create(), value);
    }));
  }

  public update(model: M): Observable<boolean> {
    return this.httpClient.put<boolean>(`${this.endpoint}/${model.uuid}`, model);
  }

  public delete(uuid: string): Observable<boolean> {
    return this.httpClient.delete<boolean>(`${this.endpoint}/${uuid}`);
  }
}
